// src/manager/pages/DistributeCredits.tsx — FINAL COMPLETE VERSION
// Real-time sync, toast feedback, safety limits, RTP warning, clean UI

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { useManager } from '../hooks/useManager';
import { toast } from 'react-hot-toast';

interface FormData {
  user_login_id: string;
  amount: number;
}

export default function DistributeCredits() {
  const { distributeCredits, fetchUsers, fetchAccount, fetchCreditTransactions, socket } = useManager();
  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [account, setAccount] = useState<any>(null);
  const [transactions, setTransactions] = useState<any[]>([]);

  useEffect(() => {
    fetchAccount().then(setAccount).catch(() => {});
    fetchCreditTransactions().then((rows: any[]) => setTransactions((rows || []).slice(0, 8))).catch(() => {});
  }, [fetchAccount, fetchCreditTransactions]);

  // Real-time credits update sync
  useEffect(() => {
    if (!socket) return;

    const handleCreditsUpdated = () => {
      toast.success('Credits updated in real-time!', { duration: 4000 });
      fetchUsers().catch(() => toast.error('Failed to refresh users list'));
    };

    socket.on('credits-updated', handleCreditsUpdated);

    return () => {
      socket.off('credits-updated', handleCreditsUpdated);
    };
  }, [socket, fetchUsers]);

  const onSubmit = async (data: FormData) => {
    setError('');
    setLoading(true);

    // Safety limit: ±10,000 at once (prevent accidental large changes)
    if (data.amount > 10000 || data.amount < -10000) {
      setError('Amount must be between -10,000 and 10,000');
      toast.error('Amount must be between -10,000 and 10,000');
      setLoading(false);
      return;
    }

    // RTP Warning for real credits (positive amount only)
    if (data.amount > 0) {
      if (!window.confirm('Adding real credits will affect RTP exposure and house balance. Confirm?')) {
        setLoading(false);
        return;
      }
    }

    try {
      await distributeCredits({
        user_email: data.user_login_id.trim(),
        amount: Number(data.amount),
      });

      await fetchUsers(); // Refresh user list after success
      const nextAccount = await fetchAccount();
      setAccount(nextAccount);
      const nextTransactions = await fetchCreditTransactions();
      setTransactions((nextTransactions || []).slice(0, 8));
      reset(); // Clear form
      toast.success(`Successfully ${data.amount > 0 ? 'added' : 'removed'} ${Math.abs(data.amount)} credits!`);
    } catch (err: any) {
      const msg = err.message || 'Failed to distribute credits';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-900">Distribute Credits</h2>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-700 hover:text-red-900 font-bold">
            ✕
          </button>
        </div>
      )}

      <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
        {account && (
          <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-indigo-50 border border-indigo-200">
              <div className="text-sm text-indigo-600">Manager Balance</div>
              <div className="text-2xl font-bold text-indigo-900">{Number(account.credits || 0).toFixed(0)}</div>
            </div>
            <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
              <div className="text-sm text-emerald-600">Total Given</div>
              <div className="text-2xl font-bold text-emerald-900">{Number(account.distributed_total || 0).toFixed(0)}</div>
            </div>
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
              <div className="text-sm text-amber-600">Total Deducted Back</div>
              <div className="text-2xl font-bold text-amber-900">{Number(account.deducted_total || 0).toFixed(0)}</div>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label htmlFor="user_login_id" className="block text-sm font-medium text-gray-700 mb-1">
              User Login ID
            </label>
            <input
              id="user_login_id"
              placeholder="e.g. USR123456"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600"
              {...register('user_login_id', { required: 'Login ID required' })}
            />
            {errors.user_login_id && <p className="mt-1 text-sm text-red-600">{errors.user_login_id.message}</p>}
          </div>

          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
              Amount (positive = add, negative = remove)
            </label>
            <input
              id="amount"
              type="number"
              placeholder="e.g. 500 or -200"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600"
              {...register('amount', {
                required: 'Amount required',
                valueAsNumber: true,
              })}
            />
            {errors.amount && <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>}
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-6 font-medium rounded-lg text-white transition shadow ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2'
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <Loader2 className="animate-spin mr-2" size={20} />
                Distributing...
              </span>
            ) : (
              'Distribute Credits'
            )}
          </button>
        </form>

        {/* RTP Note */}
        <p className="mt-6 text-sm text-gray-500 text-center">
          Note: Adding real credits affects house RTP exposure. Use carefully.
        </p>

        {transactions.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Recent Credit Ledger</h3>
            <div className="space-y-2">
              {transactions.map((tx) => (
                <div key={tx.id} className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 text-sm">
                  <div>
                    <div className="font-medium text-gray-900">{tx.to_user_login_id || tx.to_user_email || 'Manager Ledger'}</div>
                    <div className="text-gray-500">{tx.type}</div>
                  </div>
                  <div className={`font-semibold ${Number(tx.amount) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {Number(tx.amount) >= 0 ? '+' : ''}{Number(tx.amount).toFixed(0)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
